import { useShowNotification } from '@inflearn/ds-react-next';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  API_ENDPOINT,
  useClientApiV1CartCreateMutation,
  useClientApiV1CartDeleteMutation,
} from '../../resources/@types/courseSchema';
import { ResponseStatus } from '../../resources/@types/schema';
import { ApiError } from '../../resources/ApiError';

type Props = {
  onAddToCartSuccess?: () => void;
  onAddToCartError?: (error: unknown) => void;
  onRemoveFromCartSuccess?: () => void;
  onRemoveFromCartError?: (error: unknown) => void;
  invalidatedQueryKey?: (string | unknown)[];
};

/** AppShell 헤더의 장바구니 invalidate를 위한 KEY */
const CART_KEY = '/v1/cart';

export const useCart = ({
  onAddToCartError,
  onAddToCartSuccess,
  onRemoveFromCartError,
  onRemoveFromCartSuccess,
  invalidatedQueryKey = [API_ENDPOINT.CLIENT_V1_COURSE_LIKES],
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { showNotification } = useShowNotification();

  const { mutateAsync: addToCart } = useClientApiV1CartCreateMutation({
    url: API_ENDPOINT.CLIENT_V1_CART,
    options: {
      onSuccess: () => {
        showNotification({
          type: 'success',
          title: t('수강바구니에 추가되었습니다.'),
        });

        queryClient.invalidateQueries(invalidatedQueryKey);
        queryClient.invalidateQueries([CART_KEY]);

        onAddToCartSuccess?.();
      },
      onError: (error) => {
        if (ApiError.is(error, ResponseStatus.BAD_REQUEST)) {
          showNotification({
            type: 'error',
            title: t('공개되지 않은 강의로 수강이 제한됩니다.'),
          });

          return;
        }

        showNotification({
          type: 'error',
          title: t('수강바구니 추가에 실패했습니다.'),
          message: t('잠시 후 다시 시도해주세요.'),
        });

        onAddToCartError?.(error);
      },
    },
  });

  const { mutateAsync: removeFromCart } = useClientApiV1CartDeleteMutation({
    url: API_ENDPOINT.CLIENT_V1_CART,
    options: {
      onSuccess: () => {
        showNotification({
          type: 'success',
          title: t('수강바구니에서 제거되었습니다.'),
        });

        queryClient.invalidateQueries(invalidatedQueryKey);
        queryClient.invalidateQueries([CART_KEY]);

        onRemoveFromCartSuccess?.();
      },
      onError: (error) => {
        if (ApiError.is(error, ResponseStatus.BAD_REQUEST)) {
          showNotification({
            type: 'error',
            title: t('공개되지 않은 강의로 수강이 제한됩니다.'),
          });

          return;
        }

        showNotification({
          type: 'error',
          title: t('수강바구니 삭제에 실패했습니다.'),
          message: t('잠시 후 다시 시도해주세요.'),
        });

        onRemoveFromCartError?.(error);
      },
    },
  });

  return { addToCart, removeFromCart };
};
